
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function SustainabilityPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section 
        className="relative h-96 bg-cover bg-center bg-no-repeat flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/c87240906226d9f33e507e5c2fda95c4.jpg')`
        }}
      >
        <div className="text-center text-white max-w-4xl mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6">Sustainability & Environmental Stewardship</h1>
          <p className="text-xl leading-relaxed">
            Committed to preserving nature while creating exceptional glamping experiences through sustainable practices and environmental responsibility.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Our Commitment */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Environmental Commitment</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              At OED Glamping, we believe luxury and sustainability go hand in hand. Every development is designed to minimize environmental impact while maximizing guest experience.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-leaf-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Carbon Neutral</h3>
              <p className="text-gray-600">All our developments are designed to achieve carbon neutrality through renewable energy and sustainable practices.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-recycle-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Zero Waste</h3>
              <p className="text-gray-600">Comprehensive waste reduction and recycling programs ensure minimal environmental footprint.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-water-percent-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Water Conservation</h3>
              <p className="text-gray-600">Advanced water management systems reduce consumption and protect local water resources.</p>
            </div>
          </div>
        </section>

        {/* Sustainable Practices */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Sustainable Practices</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=eco-friendly%20glamping%20construction%20with%20sustainable%20building%20materials%2C%20solar%20panels%2C%20green%20roof%20systems%2C%20natural%20wood%20and%20stone%20materials%2C%20environmentally%20conscious%20development%20in%20forest%20setting&width=600&height=400&seq=sustainable-practices&orientation=landscape"
                alt="Sustainable Construction Practices"
                className="w-full h-80 object-cover rounded-lg shadow-lg"
              />
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-sun-line text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Renewable Energy</h3>
                  <p className="text-gray-600">Solar panels and wind systems power all accommodations with clean, renewable energy sources.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-building-line text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Sustainable Materials</h3>
                  <p className="text-gray-600">Locally sourced, eco-friendly building materials minimize transportation impact and support local economies.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-plant-line text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Habitat Preservation</h3>
                  <p className="text-gray-600">Careful site planning preserves existing ecosystems and native wildlife habitats.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-drop-line text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Water Management</h3>
                  <p className="text-gray-600">Rainwater harvesting and greywater recycling systems reduce water consumption by up to 40%.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Environmental Impact */}
        <section className="mb-16 bg-gray-50 rounded-lg p-12">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Environmental Impact</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-600 mb-2">85%</div>
              <p className="text-gray-600">Reduction in carbon emissions compared to traditional hotels</p>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-600 mb-2">40%</div>
              <p className="text-gray-600">Less water consumption through conservation systems</p>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-600 mb-2">95%</div>
              <p className="text-gray-600">Waste diverted from landfills through recycling programs</p>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-600 mb-2">100%</div>
              <p className="text-gray-600">Renewable energy powering all accommodations</p>
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">Certifications & Partnerships</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-award-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">LEED Certified</h3>
              <p className="text-gray-600">All developments meet Leadership in Energy and Environmental Design standards.</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-earth-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Carbon Trust</h3>
              <p className="text-gray-600">Verified carbon footprint reduction and environmental management systems.</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Green Key</h3>
              <p className="text-gray-600">International eco-label for tourism accommodations and attractions.</p>
            </div>
          </div>
        </section>
      </div>
      
      <Footer />
    </div>
  );
}